import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import { StepValue, stepsOf } from 'features/datasets/components/EvalConditions';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';
import { loadEvalGraph } from 'shared/components/filters/evals/evalLineCache';

/**
 * One eval condition as a filter chip, worn like InlineFilterChip: the eval
 * (and step) in place of the field, then what it must have said. Edits the
 * same line shape EvalConditions does.
 */

const VERDICT_OPS = [
  { value: 'fail', label: 'failed' }, { value: 'pass', label: 'passed' }, { value: 'not_fail', label: 'did not fail' },
  { value: 'wrong', label: 'marked wrong' }, { value: 'not_wrong', label: 'not marked wrong' }, { value: 'right', label: 'marked right' },
];
const NUM_OPS = [['lt', '<'], ['lte', '≤'], ['gte', '≥'], ['gt', '>'], ['eq', '=']].map(([value, label]) => ({ value, label }));

export default function EvalFilterChip({ line, evalName, isFirst = false, onChange, onRemove, teamId: teamIdArg }) {
  const teamId = getSelectedTeamNumericId() || teamIdArg;
  const [graph, setGraph] = useState(null);
  useEffect(() => {
    if (line.what !== 'step' || !line.lineage_id) return undefined;
    let off = false;
    loadEvalGraph(teamId, line.lineage_id).then((g) => { if (!off) setGraph(g); });
    return () => { off = true; };
  }, [line.what, line.lineage_id, teamId]);

  const step = line.what === 'step' ? stepsOf(graph).find((s) => s.id === line.node_id) : null;
  const fieldDef = step && line.field ? { name: line.field, ...(step.output[line.field] || {}) } : null;
  const name = evalName || (line.lineage_id ? 'Eval' : 'Any eval');
  const label = line.what === 'step' ? `${name} › ${step ? step.name : line.node_id} › ${line.field}`
    : line.what === 'score' ? `${name} · score` : line.what === 'reason' ? `${name} · reason code` : name;
  const set = (patch) => onChange({ ...line, ...patch });

  let editor = null;
  if (line.what === 'score') {
    editor = (
      <>
        <div style={{ minWidth: 56 }}><BrandDropdown value={line.op || 'lt'} onChange={(op) => set({ op })} options={NUM_OPS} compact /></div>
        <input data-chip-control type="number" step="any" aria-label="Score" value={line.value ?? ''} placeholder="0–1"
          onChange={(e) => set({ value: e.target.value })}
          className="h-[26px] w-16 rounded-md border px-1.5 text-[12px] text-[color:var(--text-primary)] outline-none" />
      </>
    );
  } else if (line.what === 'reason') {
    editor = (
      <input data-chip-control aria-label="Reason codes" value={line.reason || ''} placeholder="UNGROUNDED, TOO_LONG"
        onChange={(e) => set({ reason: e.target.value })}
        className="h-[26px] w-44 rounded-md border px-1.5 text-[12px] text-[color:var(--text-primary)] outline-none" />
    );
  } else if (line.what === 'step') {
    editor = fieldDef ? (
      <StepValue field={fieldDef} value={line.stepValue} op={line.op}
        onValue={(v) => set({ stepValue: v })} onOp={(op) => set({ op })} />
    ) : <span className="px-1 text-[12px] text-[color:var(--text-muted)]">{graph ? 'step not in the current version' : '…'}</span>;
  } else {
    editor = <div style={{ minWidth: 120 }}><BrandDropdown value={line.what} onChange={(what) => set({ what })} options={VERDICT_OPS} compact menuMinWidth={180} /></div>;
  }

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Eval condition">
      <span className="w-10 px-0.5 text-[11px] text-[color:var(--text-muted)]">{isFirst ? 'where' : 'and'}</span>
      <div className="trodo-chip inline-flex items-center gap-1 rounded-lg border border-[color:var(--border-default)] bg-surface py-0.5 pl-2.5 pr-1">
        <span className="text-[12px] font-medium text-[color:var(--text-primary)]">{label}</span>
        {editor}
        <style>{`
          .trodo-chip .brand-dropdown-trigger, .trodo-chip [data-chip-control] { border-color: transparent !important; background: transparent !important; height: 26px; }
          .trodo-chip .brand-dropdown-trigger:hover, .trodo-chip [data-chip-control]:hover { background: var(--surface-hover) !important; }
        `}</style>
        <button type="button" onClick={onRemove} aria-label="Remove filter" title="Remove filter"
          className="ml-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
          <X size={13} />
        </button>
      </div>
    </div>
  );
}
